// @givmo/org-console — Reload page after a stale save (PE-WEB18; D-PE-45, D-PE-46): the PURE merge.
//
// A save the server refused because the page changed after this draft was read (its version is no
// longer the page's) is recovered in place: Reload page reads the page again and keeps every
// unsaved change the latest page does not contradict, naming what it could not keep. This file is
// that merge and nothing else — it performs no I/O, so every rule is pinned without a screen.
//
// ══ ONE MERGE, THE IOS EDITOR'S RULES ══════════════════════════════════════════════════════════
//
// The rules are the iOS editor's, ruled by Jonathan as drawn (D-PE-45, 2026-10-14): this is a port
// of `ProfilePageDraft.reloaded` (givmo-ios `Givmo/Features/ProfilePageEditor/ProfilePageDraft.swift`),
// function for function, and `profile-reload.test.ts` holds the same cases as the iOS
// `ProfilePageDraftTests` reload tests, each named for its twin. A rule changed in one place changes
// in both.
//
// For each block the draft read: the person's edit stays unless the block was removed, changed or
// taken down elsewhere (the latest version stays, and the note says so) — an edit the latest page
// already holds word for word loses nothing and is not named; their removal stays unless the block
// was changed or relisted elsewhere (it comes back), and a takedown alone does not bring it back
// (both take it off the public page); their order stays unless blocks were moved elsewhere too, and
// is named lost only where it differs from the latest page's. Their new blocks stay, after the block
// each followed; a block added elsewhere sits after the one it follows on the latest page. Nothing
// is re-created from an id the server no longer has.

import type { ProfilePageOut } from '@givmo/api-client';
import { videoProviderLabel } from '@givmo/design-system';
import { PROFILE_COPY } from './profile-copy';
import {
  blockInput,
  draftFromWire,
  isUnlisted,
  lockedBlockInput,
  type DraftBlock,
} from './profile-model';

/** What Reload page took out of the draft: each item is said above the blocks until the next save. */
export type ReloadNoticeItem =
  /** A block the draft held was removed elsewhere; `hadEdits`: with the person's unsaved edits to it. */
  | { kind: 'removed'; name: string; hadEdits: boolean }
  /** The person's edits to a block that was changed elsewhere: the latest version stays. */
  | { kind: 'changed'; name: string }
  /** A block the person removed was changed elsewhere, so it is back on the page. */
  | { kind: 'back'; name: string }
  /** Blocks were moved elsewhere too, so the person's order was not kept. */
  | { kind: 'order' };

export interface ReloadedDraft {
  /** The draft after the reload: the latest page with every change of the person's it keeps. */
  readonly rows: DraftBlock[];
  /** What it could not keep, in the order the note says it. */
  readonly notice: ReloadNoticeItem[];
  /** The rows whose content is still the person's own (their pictures and upload state stay with them). */
  readonly personalKeys: ReadonlySet<string>;
}

/**
 * Reload page (D-PE-45 choice A): the page as `latest` serves it, with every unsaved change of
 * `draft` — made against `base`, the page as last read or saved — that the latest page does not
 * contradict.
 */
export function reloadDraft(
  draft: readonly DraftBlock[],
  base: readonly DraftBlock[],
  latest: ProfilePageOut,
): ReloadedDraft {
  const latestRows = draftFromWire(latest);
  const baseById = byServerId(base);
  const latestById = byServerId(latestRows);
  const draftById = byServerId(draft);

  const edited = (row: DraftBlock): boolean => {
    if (row.id === null) return false;
    const original = baseById.get(row.id);
    return original !== undefined && !sameInput(row, original);
  };
  const changedElsewhere = (id: string): boolean => {
    const original = baseById.get(id);
    const now = latestById.get(id);
    if (original === undefined || now === undefined) return false;
    return !sameInput(now, original) || isUnlisted(now) !== isUnlisted(original);
  };
  /** Elsewhere the block was only taken down: listed when read, taken down now, its content unchanged. */
  const onlyTakenDown = (original: DraftBlock, now: DraftBlock): boolean =>
    !isUnlisted(original) &&
    isUnlisted(now) &&
    JSON.stringify(lockedBlockInput(original)) === JSON.stringify(lockedBlockInput(now));

  const notice: ReloadNoticeItem[] = [];
  const personalKeys = new Set<string>();
  // Each draft row as the reload leaves it, by its key; a dropped row is absent.
  const resolved = new Map<string, DraftBlock>();
  for (const row of draft) {
    if (row.id === null) {
      resolved.set(row.key, row);
      personalKeys.add(row.key);
      continue;
    }
    const name = reloadBlockName(row);
    const now = latestById.get(row.id);
    if (now === undefined) {
      notice.push({ kind: 'removed', name, hadEdits: edited(row) });
      continue;
    }
    if (!edited(row)) {
      resolved.set(row.key, withKey(now, row.key));
    } else if (sameInput(row, now)) {
      // The latest page already holds exactly this edit: nothing of the person's is dropped.
      resolved.set(row.key, withKey(now, row.key));
    } else if (changedElsewhere(row.id)) {
      notice.push({ kind: 'changed', name });
      resolved.set(row.key, withKey(now, row.key));
    } else {
      resolved.set(row.key, withServerState(row, now));
      personalKeys.add(row.key);
    }
  }

  // A block the person removed comes back only if the latest page contradicts the removal: its
  // content changed or it was relisted elsewhere. A takedown alone takes it off the public page as
  // the removal does, so the removal stays.
  const back = new Set<string>();
  for (const original of base) {
    const id = original.id;
    if (id === null || draftById.has(id)) continue;
    const now = latestById.get(id);
    if (now === undefined || !changedElsewhere(id) || onlyTakenDown(original, now)) continue;
    back.add(id);
    notice.push({ kind: 'back', name: reloadBlockName(now) });
  }
  /** A latest block the draft never held: added elsewhere, or back. */
  const isTheirs = (id: string): boolean => !baseById.has(id) || back.has(id);

  const baseOrder = serverIds(base);
  const draftOrder = serverIds(draft);
  const latestOrder = serverIds(latestRows);
  const iMoved = movedRelative(baseOrder, draftOrder);
  const theyMoved = movedRelative(baseOrder, latestOrder);
  // Named only where the person's order differs from the latest page's: the same move made on both
  // sides loses nothing.
  if (iMoved && theyMoved && movedRelative(latestOrder, draftOrder)) notice.push({ kind: 'order' });

  const result: DraftBlock[] = [];
  if (iMoved && !theyMoved) {
    // The person's order; each block of the latest page's own after the one it follows there.
    for (const row of draft) {
      const kept = resolved.get(row.key);
      if (kept !== undefined) result.push(kept);
    }
    let insertAt = 0;
    for (const now of latestRows) {
      const id = now.id;
      if (id === null) continue;
      const mine = draftById.get(id);
      const index = mine === undefined ? -1 : result.findIndex((row) => row.key === mine.key);
      if (index >= 0) {
        insertAt = index + 1;
      } else if (isTheirs(id)) {
        result.splice(insertAt, 0, now);
        insertAt += 1;
      }
    }
  } else {
    // The latest order; each new block of the person's after the one it followed.
    for (const now of latestRows) {
      const id = now.id;
      if (id === null) continue;
      const mine = draftById.get(id);
      if (mine !== undefined) {
        const kept = resolved.get(mine.key);
        if (kept !== undefined) result.push(kept);
      } else if (isTheirs(id)) {
        result.push(now);
      }
    }
    let insertAt = 0;
    for (const row of draft) {
      if (row.id === null) {
        result.splice(insertAt, 0, row);
        insertAt += 1;
      } else {
        const index = result.findIndex((kept) => kept.key === row.key);
        if (index >= 0) insertAt = index + 1;
      }
    }
  }
  return { rows: result, notice, personalKeys };
}

/** The note's line for one item, in D-PE-45's words. */
export function reloadNoticeLine(item: ReloadNoticeItem): string {
  const copy = PROFILE_COPY.blocks.reloaded;
  switch (item.kind) {
    case 'removed':
      return item.hadEdits ? copy.removedWithEdits(item.name) : copy.removed(item.name);
    case 'changed':
      return copy.changed(item.name);
    case 'back':
      return copy.back(item.name);
    case 'order':
      return copy.order;
  }
}

/**
 * How a line names a block: its kind and its own words, as the person last saw it — never a block
 * number, which the reload moves. A block's own words are cut at a word to stay one short line.
 */
export function reloadBlockName(row: DraftBlock): string {
  const names = PROFILE_COPY.blocks.reloaded.names;
  const title = excerpt(row.title, 40);
  switch (row.kind) {
    case 'story':
      return names.story(title ?? excerpt(row.body, 40));
    case 'image':
      return names.image(title);
    case 'video':
      return names.video(videoProviderLabel(row.mediaUrl) ?? providerForPageUrl(row.mediaUrl));
    case 'link':
      return names.link(title);
    default:
      return names.other;
  }
}

/**
 * The first `limit` characters of `text` on one line (whitespace runs collapsed), cut back to the
 * last word boundary with an ellipsis when it is longer; `null` when nothing is left. Counted in
 * characters as a person sees them (grapheme clusters), as the iOS editor counts them.
 */
export function excerpt(text: string, limit: number): string | null {
  const flat = text
    .split(/\s+/u)
    .filter((part) => part.length > 0)
    .join(' ');
  if (flat.length === 0) return null;
  const graphemes = Array.from(
    new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(flat),
    (s) => s.segment,
  );
  if (graphemes.length <= limit) return flat;
  const slice = graphemes.slice(0, limit).join('');
  const space = slice.lastIndexOf(' ');
  const cut = space >= 0 ? slice.slice(0, space) : slice;
  return `${cut.trim()}…`;
}

/** The provider a typed page link names, before the server canonicalizes it. */
function providerForPageUrl(value: string): 'YouTube' | 'Vimeo' | null {
  let host: string;
  try {
    host = new URL(value.trim()).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host.includes('youtube') || host === 'youtu.be') return 'YouTube';
  if (host.includes('vimeo')) return 'Vimeo';
  return null;
}

function sameInput(a: DraftBlock, b: DraftBlock): boolean {
  return JSON.stringify(blockInput(a)) === JSON.stringify(blockInput(b));
}

/** The latest row under the draft's key (Reload keeps each kept row's key, so its photo's state stays with it). */
function withKey(row: DraftBlock, key: string): DraftBlock {
  return { ...row, key };
}

/** The person's edits on the latest state of the same block (its moderation, its poster, the values it was read with). */
function withServerState(row: DraftBlock, latest: DraftBlock): DraftBlock {
  return {
    ...row,
    id: latest.id,
    unlistedAt: latest.unlistedAt,
    unlistedReason: latest.unlistedReason,
    posterUrl: latest.posterUrl,
    wireTitle: latest.wireTitle,
    wireBody: latest.wireBody,
  };
}

function byServerId(rows: readonly DraftBlock[]): Map<string, DraftBlock> {
  const map = new Map<string, DraftBlock>();
  for (const row of rows) {
    if (row.id !== null && !map.has(row.id)) map.set(row.id, row);
  }
  return map;
}

function serverIds(rows: readonly DraftBlock[]): string[] {
  return rows.flatMap((row) => (row.id === null ? [] : [row.id]));
}

/** Whether `order` moved the blocks it shares with `baseOrder` relative to each other (a removal or an addition alone moves nothing). */
function movedRelative(baseOrder: readonly string[], order: readonly string[]): boolean {
  const orderSet = new Set(order);
  const shared = new Set(baseOrder.filter((id) => orderSet.has(id)));
  const mine = order.filter((id) => shared.has(id));
  const theirs = baseOrder.filter((id) => shared.has(id));
  return mine.length !== theirs.length || mine.some((id, index) => id !== theirs[index]);
}
